
// * ========================================================================
// * ages per day chart and table
// * ========================================================================

import CovidChart from './covid-chart.js';
import moment from 'moment';
import storage from './../storage';

const sewageCompare = (function(){
    let thisChart = null;
    let chartData = null;
    let selectedDatasets;

    const average = (arr) => arr.reduce((a, b) => a + b) / arr.length;

    function createChartData(data, options) {
        const datasets = [];
        const multiselectOptions = [];
        let latestValue = moment('2019-12-12');

        let datasetIdx = 0;
        Object.values(data.locations).forEach(location => {
            const latestValueDate = moment(location.weeks[0].date);
            const secondLatestValueDate = location.weeks.length > 1 ? moment(location.weeks[1].date): false;// We need a line
            if (latestValueDate.isAfter(latestValue)) {
                latestValue = latestValueDate;
            }

            if (secondLatestValueDate && secondLatestValueDate >= options.firstDate) {
                const name = location.city + ', ' + location.name;
                const isSelected = selectedDatasets.indexOf(location.name) !== -1;

                const locationColor = options.largeColorPalette[datasetIdx];

                const locationDataset = {
                    type: 'LineWithLine',
                    label: name,
                    name: name,
                    id: location.name,
                    backgroundColor: locationColor,
                    fill: false,
                    borderColor: locationColor,
                    borderWidth: 3,
                    /*pointBorderWidth: 2,
                    pointHoverBorderWidth: 2,*/
                    data: [],
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    cubicInterpolationMode: 'default',
                    lineTension: 0.2,
                    yAxisID: 'y',
                    selected: isSelected,
                    latestValueDate: latestValueDate
                }

                multiselectOptions.push(locationDataset);
                datasets.push(locationDataset);

                // New data
                const currentDataset = datasets[datasets.length - 1];

                const oldProcessingDate = moment('2022-06-27').set({
                    hour: 0,
                    minute: 0,
                    second: 0
                });
                const newProcessingDate = moment('2023-01-01').set({
                    hour: 0,
                    minute: 0,
                    second: 0
                });

                for (let idx = (location.weeks.length - 1); idx >= 0; idx--) {
                    const item = location.weeks[idx];
                    const date = moment(item.date);

                    // Push only after first visible date
                    if (date >= options.firstDate) {
                        let firstDay = idx + 1;
                        firstDay = firstDay > (location.weeks.length - 1) ? idx : firstDay;

                        const pastWeekValues = [];
                        const pastWeekValuesOld = [];
                        const pastWeekValuesOldest = [];
                        for (let x = idx; x <= firstDay; x++) {
                            if (location.weeks[x].count !== null) {
                                if (moment(location.weeks[x].date) >= newProcessingDate) {
                                    pastWeekValues.push(location.weeks[x].count);
                                } else {
                                    pastWeekValuesOld.push(location.weeks[x].count);
                                }
                            }
                            if (location.weeks[x].countOld !== null) {
                                pastWeekValuesOldest.push(location.weeks[x].countOldEvened);
                            }
                        }

                        if (date >= newProcessingDate) {
                            let weeksAverage = pastWeekValues.length ? average(pastWeekValues) : 0;
                            weeksAverage = Math.round(weeksAverage);

                            // Add to average count
                            currentDataset.data.push({
                                x: date,
                                y: pastWeekValues.length && item.date !== '2022-06-20' ? weeksAverage : NaN
                            });

                        } else if (date >= oldProcessingDate) {
                            // Old method
                            let weeksAverageOld = pastWeekValuesOld.length ? average(pastWeekValuesOld) : 0;
                            weeksAverageOld = Math.round(weeksAverageOld);

                            // Add to average count
                            currentDataset.data.push({
                                x: date,
                                y: pastWeekValuesOld.length && item.date !== '2022-06-20' ? weeksAverageOld : NaN
                            });
                        } else {
                            // Oldest method
                            const weeksAverageOldest = pastWeekValuesOldest.length ? average(pastWeekValuesOldest) : 0;

                            // Add to average count
                            currentDataset.data.push({
                                x: date,
                                y: pastWeekValuesOldest.length ? Math.round(weeksAverageOldest) : NaN
                            });
                        }
                    }
                }

                datasetIdx++;
            }
        });

        return { datasets, multiselectOptions, latestValue };
    }

    function getSelected() {
        // Initially selected
        let selected = storage.getData('sewage-compare-chart_multiselect');
        if (!selected || !Array.isArray(selected) || !selected.length) {
            selected = ['Kakolanmäki', 'Viikinmäki'];
        }
        selectedDatasets = selected;

        return selected;
    }

    /**
     * Initialize
     *
     * @param {Object} data - covid data
     */
    function init(data) {
        chartData = data;

        // Initially selected
        const selected = getSelected();

        // Get data or use old datasets
        let { datasets, multiselectOptions, latestValue } = createChartData(chartData, window.config, selected);

        // Sort options into latest date group & alphabetically
        multiselectOptions.sort(function(a,b) {
            if (a.latestValueDate.isSame(b.latestValueDate)) {
                return a.name.localeCompare(b.name);
            } else {
                return a.latestValueDate.isAfter(b.latestValueDate) ? 1 : -1;
            }
        });

        const primaryMultiselectOptions = [];
        const secondaryMultiselectOptions = [];
        for (const option of multiselectOptions) {
            if (option.latestValueDate.isSame(latestValue)) {
                primaryMultiselectOptions.push(option);
            } else {
                secondaryMultiselectOptions.push(option);
            }
        }

        const chartOptions = {
            datasets,
            chartOptions: {
                type: 'LineWithLine',
                options: {
                    interaction: {
                        mode: 'x',
                        intersect: false
                    },
                    plugins: {
                        tooltip: {
                            intersect: false,
                            position: 'nearest',
                            bodySpacing: 5,
                            callbacks: {
                                label: function(tooltipItem) {
                                    let label = tooltipItem.dataset.label + ': ' || '';
                                    label = label + new Intl.NumberFormat('fi-FI').format(tooltipItem.raw.y);

                                    return label;
                                }
                            },
                            itemSort: function(a, b, data) {
                                return b.parsed.y > a.parsed.y ? 1 : -1;
                            },
                        },
                    },
                    scales: {
                        x: {
                            type: 'time',
                            stacked: true,
                            offset: true,
                            time: {
                                unit: 'day',
                                displayFormats: {
                                    day: 'D.M.YY',
                                }
                            }
                        },
                        y: {
                            stacked: false,
                            type: 'linear',
                            position: 'right',
                            ticks: {
                                min: 0
                            },
                            title: {
                                display: true,
                                text: 'Miljoonaa virus-RNA kopiota / 1000 hlö / vrk'
                            }
                        }
                    }
                }
            },
            config: window.config,
            tooltips: {
                elementId: 'sewage-compare-chart-tooltip'
            },
            multiselect: {
                requireCount: false,
                valueType: 'string',
                idField: 'id',
                colorField: 'borderColor',
                inputName: 'sewage-compare-multiselect',
                selectElemId: 'sewage-compare-multiselect',
                secondarySelectElemId: 'sewage-compare-chart-multiselect-old',
                sectionTitleClass: 'sewage-compare-chart-multiselect-section-title',
                colorElemId: 'sewage-compare-multiselect-colors',
                countElemId: 'sewage-compare-multiselect-count',
                selectAllElemId: 'sewage-compare-multiselect-select-all',
                clearAllElemId: 'sewage-compare-multiselect-clear-all',
                multiselectOptions: primaryMultiselectOptions,
                secondaryMultiselectOptions: secondaryMultiselectOptions
            },
            responsive: ['aspectRatio']
        };

        thisChart = new CovidChart('sewage-compare-chart', chartOptions);
    }

    function reload(data = null) {
        if (thisChart) {
            // Rebuild the whole thing
            thisChart.chart.destroy();
            thisChart = null;

            // remove all content
            const chartContainer = document.querySelector('.sewage-compare-chart .chart-container');
            chartContainer.innerHTML = '';

            // Remove multiselects
            const multiSelectContainers = document.querySelectorAll('.sewage-compare-chart .multiselect');
            multiSelectContainers.forEach((elem) => {
                elem.innerHTML = '';
            });

            // Re create tooltip elem for same reason
            const tooltip = document.createElement('div');
            tooltip.classList.add('chart-tooltip');
            tooltip.id = 'sewage-compare-chart-tooltip';

            // Re-create canvas
            const canvasEl = document.createElement('canvas');
            canvasEl.id = 'sewage-compare-chart';
            canvasEl.width = 888;
            canvasEl.height= 440;

            chartContainer.appendChild(canvasEl);
            chartContainer.appendChild(tooltip);
        }

        init(data);
    }

    return {
        init,
        reload
    }

})();

export default sewageCompare;
